/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Formatting for audit values.
 *
 * Timestamps arrive as UTC ISO strings and are rendered in the user's timezone — the same zone the
 * API uses, resolved through the shared `getUserTimezone`. A "last 24 hours" computed in UTC is
 * wrong for every user outside it, so the time-range presets resolve here too.
 */

import { DateTime } from 'luxon'

import { getUserTimezone } from '@/insights/common/timezone'

import type { AuditFilters, TimePreset } from './types'

const zoned = (iso: string) => DateTime.fromISO(iso, { zone: 'utc' }).setZone(getUserTimezone())

/** `30 Jul 2026` */
export const formatDate = (iso: string): string => zoned(iso).toFormat('dd LLL yyyy')

/** `09:28 AM` — the list. */
export const formatTime = (iso: string): string => zoned(iso).toFormat('hh:mm a')

/** `30 Jul 2026, 09:28:11 AM` — the detail panel. */
export const formatDateTime = (iso: string): string => zoned(iso).toFormat('dd LLL yyyy, hh:mm:ss a')

/** `2 hours ago` — tooltips only. */
export const formatRelative = (iso: string): string => zoned(iso).toRelative() ?? ''

export const formatUtc = (iso: string): string =>
  `${DateTime.fromISO(iso, { zone: 'utc' }).toFormat('dd LLL yyyy, HH:mm:ss')} UTC`

/** Thousand-separated, e.g. `1,487`. */
export const formatCount = (n: number): string => n.toLocaleString('en-US')

/**
 * The name to show for a user, and whether it was resolved.
 *
 * An unresolved identifier is shown as `User #1071` in muted italic rather than left blank — the
 * fact that there *was* an actor is the point of the row.
 */
export const displayUser = (userName: string | null, userId: string): { text: string; resolved: boolean } =>
  userName ? { text: userName, resolved: true } : { text: `User #${userId}`, resolved: false }

export const displayCompany = (companyName: string | null, companyId: number): string =>
  companyName ?? `Company #${companyId}`

const PRESET_DURATIONS: Record<Exclude<TimePreset, 'custom'>, Parameters<DateTime['minus']>[0]> = {
  '24h': { hours: 24 },
  '7d': { days: 7 },
  '30d': { days: 30 },
  '90d': { days: 90 },
}

/**
 * Resolve the filter's time range to the absolute UTC bounds the API expects.
 *
 * Presets are computed in the user's timezone before conversion, so "last 7 days" means the same
 * thing to the user as it does on their calendar.
 */
export const resolveRange = (filters: AuditFilters): { after?: string; before?: string } => {
  if (filters.time === 'custom') {
    return { after: filters.from, before: filters.to }
  }

  const now = DateTime.now().setZone(getUserTimezone())
  return { after: now.minus(PRESET_DURATIONS[filters.time]).toUTC().toISO() ?? undefined }
}

/** `12 Jun – 18 Jun` for the custom-range button; empty when the range is incomplete. */
export const formatRangeLabel = (from?: string, to?: string): string => {
  if (!from || !to) return ''
  return `${zoned(from).toFormat('dd LLL')} – ${zoned(to).toFormat('dd LLL')}`
}

/** Convert a datetime-local input value in the user's zone to a UTC ISO string. */
export const localInputToUtc = (value: string): string | undefined =>
  value ? (DateTime.fromISO(value, { zone: getUserTimezone() }).toUTC().toISO() ?? undefined) : undefined

/** Convert a UTC ISO string to the value a datetime-local input expects. */
export const utcToLocalInput = (iso?: string): string => (iso ? zoned(iso).toFormat("yyyy-MM-dd'T'HH:mm") : '')
